import React, { useState } from "react";
import { toast } from "react-toastify";
import { FaUserTag, FaHashtag, FaSmile, FaLink, FaUserCheck } from "react-icons/fa";
import AITextAnalyzer from "../../../components/AI_Implementation/AITextAnalyzer/AITextAnalyzer";

const GATE_DEFAULTS = {
  accessPromptMessage:
    "Hey there! Glad you're here 😊\nTap below and I'll send you the access in just a moment ✨",
  accessButtonTitle: "Send me the access",
  followPromptMessage:
    "Almost there!\nPlease visit my profile and tap follow to continue 😄",
  visitProfileButtonTitle: "Visit Profile",
  followingButtonTitle: "I'm following ✅",
};

const EMPTY_GATE = {
  enabled: false,
  accessPromptMessage: "",
  accessButtonTitle: "",
  followPromptMessage: "",
  visitProfileButtonTitle: "",
  followingButtonTitle: "",
  profileUrl: "",
};

/** Pre-fill empty gate copy with defaults when the gate is enabled. */
function applyGateDefaults(gate) {
  if (!gate.enabled) return gate;
  return {
    ...gate,
    accessPromptMessage:
      gate.accessPromptMessage || GATE_DEFAULTS.accessPromptMessage,
    accessButtonTitle:
      gate.accessButtonTitle || GATE_DEFAULTS.accessButtonTitle,
    followPromptMessage:
      gate.followPromptMessage || GATE_DEFAULTS.followPromptMessage,
    visitProfileButtonTitle:
      gate.visitProfileButtonTitle || GATE_DEFAULTS.visitProfileButtonTitle,
    followingButtonTitle:
      gate.followingButtonTitle || GATE_DEFAULTS.followingButtonTitle,
  };
}

const Message = ({ onNext, data, handlePrevious, onFinalSubmit, isSubmitting, isEditMode = false }) => {
  const [message, setMessage] = useState(data?.message || "");
  const [productLink, setProductLink] = useState(data?.productLink || "");
  const [sendReplyToCommentFirst, setSendReplyToCommentFirst] = useState(
    data?.sendReplyToCommentFirst || false
  );
  const [replyCommentMessage, setReplyCommentMessage] = useState(
    data?.replyCommentMessage || "Thank you! Check your DM for the link 😊"
  );
  const [gate, setGate] = useState(() =>
    applyGateDefaults({
      ...EMPTY_GATE,
      ...(data?.followerGate || {}),
    })
  );

  // Common placeholders user can insert easily
  const placeholders = [
    { key: "{{name}}", label: "Subscriber Name", icon: <FaUserTag /> },
    { key: "{{product_link}}", label: "Product Link", icon: <FaLink /> },
    { key: "{{username}}", label: "Instagram Username", icon: <FaHashtag /> },
    { key: "{{first_name}}", label: "First Name", icon: <FaSmile /> },
  ];

  // Sync state when data prop changes (e.g., when navigating back)
  React.useEffect(() => {
    if (data?.message !== undefined) {
      setMessage(data.message || "");
    }
    if (data?.productLink !== undefined) {
      setProductLink(data.productLink || "");
    }
    if (data?.sendReplyToCommentFirst !== undefined) {
      setSendReplyToCommentFirst(!!data.sendReplyToCommentFirst);
    }
    if (data?.replyCommentMessage !== undefined) {
      setReplyCommentMessage(
        data.replyCommentMessage || "Thank you! Check your DM for the link 😊"
      );
    }
    if (data?.followerGate !== undefined) {
      setGate(
        applyGateDefaults({ ...EMPTY_GATE, ...(data.followerGate || {}) })
      );
    }
  }, [
    data?.message,
    data?.productLink,
    data?.sendReplyToCommentFirst,
    data?.replyCommentMessage,
    data?.followerGate,
  ]);

  const updateGate = (patch) => setGate((prev) => ({ ...prev, ...patch }));

  const handleInsertPlaceholder = (placeholder) => {
    setMessage((prev) => {
      const newMessage = prev.trim() ? prev + " " + placeholder : placeholder;
      return newMessage;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (sendReplyToCommentFirst && !replyCommentMessage.trim()) {
      toast.error("Please enter a reply comment message or turn off the toggle");
      return;
    }

    const normalizedReplyCommentMessage = replyCommentMessage.trim();

    const normalizedGate = applyGateDefaults(gate);

    const payload = {
      message,
      sendReplyToCommentFirst,
      replyCommentMessage: normalizedReplyCommentMessage,
      followerGate: {
        enabled: normalizedGate.enabled,
        accessPromptMessage: normalizedGate.accessPromptMessage.trim(),
        accessButtonTitle: normalizedGate.accessButtonTitle.trim(),
        followPromptMessage: normalizedGate.followPromptMessage.trim(),
        visitProfileButtonTitle: normalizedGate.visitProfileButtonTitle.trim(),
        followingButtonTitle: normalizedGate.followingButtonTitle.trim(),
        profileUrl: normalizedGate.profileUrl.trim(),
      },
    };

    if (onFinalSubmit) {
      await onFinalSubmit(payload);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-6">Direct Message Format</h1>
      <p className="text-gray-600 mb-6">
        Create a personalized message that will be sent to each subscriber’s DM.
        <br />
        Use placeholders to include dynamic fields like the subscriber’s name or username.
      </p>

      {/* Placeholder Quick Buttons */}
      <div className="flex flex-wrap gap-3 mb-4">
        {placeholders.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => handleInsertPlaceholder(p.key)}
            className="flex items-center space-x-2 px-3 py-1.5 bg-orange-100 text-orange-600 rounded-md hover:bg-orange-200 transition"
          >
            {p.icon}
            <span className="text-sm font-medium">{p.label}</span>
          </button>
        ))}
      </div>

      {/* Message Input */}
      <form onSubmit={handleSubmit}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Hi {{name}}, thanks for engaging with our post! 🎉"
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[140px]"
        />

        {/* AI Text Analyzer */}
        {message && (
          <AITextAnalyzer
            text={message}
            onSuggestion={(suggestion) => {
              setMessage(suggestion);
              toast.success("AI suggestion applied!");
            }}
            label="Message"
          />
        )}

        {/* Message Preview */}
        {message && (
          <div className="mt-4 p-4 border rounded-lg bg-gray-50">
            <h3 className="font-medium mb-2 text-gray-800">Preview Example</h3>
            <div className="text-gray-700 whitespace-pre-line">
              {message.split(/({{product_link}}|{{name}}|{{username}}|{{first_name}})/g).map((part, index) => {
                // Handle product_link placeholder - render as blue underlined link
                if (part === "{{product_link}}") {
                  if (productLink) {
                    return (
                      <a
                        key={index}
                        href={productLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline hover:text-blue-800"
                      >
                        {productLink}
                      </a>
                    );
                  } else {
                    return (
                      <span key={index} className="text-gray-400 italic">
                        [Product Link Not Set]
                      </span>
                    );
                  }
                }
                // Handle other placeholders
                if (part === "{{name}}") {
                  return <span key={index}>Alex Johnson</span>;
                }
                if (part === "{{username}}") {
                  return <span key={index}>@alex_influencer</span>;
                }
                if (part === "{{first_name}}") {
                  return <span key={index}>Alex</span>;
                }
                // Regular text - replace any remaining placeholders (shouldn't happen but just in case)
                return (
                  <span key={index}>
                    {part
                      .replace(/{{name}}/g, "Alex Johnson")
                      .replace(/{{username}}/g, "@alex_influencer")
                      .replace(/{{first_name}}/g, "Alex")}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Toggle: Send reply to comment first */}
        <div className="mt-6 border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="font-medium text-gray-800">
                Send reply to comment first
              </p>
              <p className="text-xs text-gray-500">
                When enabled, we&apos;ll reply to their comment after a successful DM.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-xs font-semibold text-gray-500">
                {sendReplyToCommentFirst ? "Yes" : "No"}
              </span>
              <button
                type="button"
                onClick={() =>
                  setSendReplyToCommentFirst((prev) => !prev)
                }
                className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-200 ${
                  sendReplyToCommentFirst
                    ? "bg-orange-500 hover:bg-orange-600"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                    sendReplyToCommentFirst ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {sendReplyToCommentFirst && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reply comment format
              </label>
              <input
                type="text"
                value={replyCommentMessage}
                onChange={(e) => setReplyCommentMessage(e.target.value)}
                placeholder="Thank you! Check your DM for the link 😊"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                This text will be posted as a public reply to the user&apos;s
                comment, only after their DM is sent successfully.
              </p>
            </div>
          )}
        </div>

        {/* Follower gate */}
        <div className="mt-6 border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="flex items-center gap-2 font-medium text-gray-800">
                <FaUserCheck className="text-orange-500" />
                Require a follow before sending the link
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Users must follow you before they receive the product link.
                We verify automatically when they tap &quot;I&apos;m following&quot;.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (gate.enabled) {
                  updateGate({ enabled: false });
                } else {
                  updateGate(applyGateDefaults({ ...gate, enabled: true }));
                }
              }}
              className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-200 ${
                gate.enabled
                  ? "bg-orange-500 hover:bg-orange-600"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                  gate.enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {gate.enabled && (
            <div className="mt-4 space-y-4 rounded-lg border border-orange-100 bg-orange-50/40 p-4">
              <p className="text-[11px] text-gray-600">
                Flow: comment → access prompt → follow gate → verify follow →
                unlock the message above. Default copy is pre-filled — edit any
                field as needed.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Step 1 — Access prompt
                </label>
                <textarea
                  rows={2}
                  value={gate.accessPromptMessage}
                  onChange={(e) =>
                    updateGate({ accessPromptMessage: e.target.value })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <input
                  type="text"
                  maxLength={20}
                  value={gate.accessButtonTitle}
                  onChange={(e) =>
                    updateGate({ accessButtonTitle: e.target.value })
                  }
                  className="mt-2 w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Step 2 — Follow gate
                </label>
                <textarea
                  rows={2}
                  value={gate.followPromptMessage}
                  onChange={(e) =>
                    updateGate({ followPromptMessage: e.target.value })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    maxLength={20}
                    value={gate.visitProfileButtonTitle}
                    onChange={(e) =>
                      updateGate({ visitProfileButtonTitle: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <input
                    type="text"
                    maxLength={20}
                    value={gate.followingButtonTitle}
                    onChange={(e) =>
                      updateGate({ followingButtonTitle: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile URL <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="url"
                  value={gate.profileUrl}
                  onChange={(e) => updateGate({ profileUrl: e.target.value })}
                  placeholder="https://www.instagram.com/yourusername"
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={handlePrevious}
            className="bg-gray-300 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-400 transition duration-300"
          >
            Back
          </button>

          <button
            type="submit"
            disabled={isSubmitting || !message || !message.trim()}
            className={`px-5 py-2 rounded-md text-white transition duration-300 ${
              isSubmitting || !message || !message.trim()
                ? "bg-orange-300 cursor-not-allowed"
                : "bg-orange-500 hover:bg-orange-600"
            }`}
          >
            {isSubmitting ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update" : "Submit")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Message;
